import type { z } from "zod"
import { useQuery } from "@tanstack/react-query"
import {
  type PnlBreakdown,
  marketplacePnlResponseSchema,
  type MarketplaceId,
  routes,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { keepPreviousForMarketplace } from "./marketplace-query"

export type { PnlBreakdown } from "@nova-ai/shared"
export type PnlMonthPoint = PnlBreakdown & { month: string }
export type MarketplacePnlResponse = z.infer<typeof marketplacePnlResponseSchema>

/** Whole-marketplace PnL over the last `months` calendar months (the server
 *  trims leading months with no data). A separate read from the revenue
 *  dashboard (offer-health / top-products pattern) so the section loads
 *  independently and never blocks the main dashboard payload. */
export function useMarketplacePnl(marketplace: MarketplaceId, months: number) {
  return useQuery({
    queryKey: ["marketplace-pnl", marketplace, months] as const,
    enabled: !!marketplace,
    // Keep a prior months window for this marketplace, but never carry one
    // shop's statement beneath another shop's heading after a route switch.
    placeholderData: keepPreviousForMarketplace(marketplace),
    queryFn: async () => {
      const result = await apiFetch<MarketplacePnlResponse>(
        routes.marketplaces.pnl({ marketplace }),
        { query: { months: String(months) } }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return marketplacePnlResponseSchema.parse(result.value)
    },
  })
}
